export type Unit = 'C' | 'F';

export function toUnit(celsius: number, unit: Unit): number {
  return Math.round(unit === 'C' ? celsius : (celsius * 9) / 5 + 32);
}

export interface DailyLike {
  min_c: number;
  max_c: number;
}

/** Range-bar geometry on the week's own min..max scale, in percent of the track. */
export function weekBars(days: DailyLike[]): { left: number; width: number }[] {
  if (!days.length) return [];
  let lo = Infinity;
  let hi = -Infinity;
  for (const d of days) {
    lo = Math.min(lo, d.min_c);
    hi = Math.max(hi, d.max_c);
  }
  const span = Math.max(hi - lo, 1);
  return days.map((d) => {
    const left = ((d.min_c - lo) / span) * 100;
    const width = Math.max(((d.max_c - d.min_c) / span) * 100, 2);
    return { left: round1(left), width: round1(Math.min(width, 100 - left)) };
  });
}

export interface Sparkline {
  line: string;
  area: string;
  nowX: number; // percent
  nowY: number; // px within height
  min: number;
  max: number;
}

/** Points for a 24-hour temperature sparkline in a w×h viewBox, scaled to the day's range. */
export function sparkline(temps: number[], fractionalHour: number, w = 240, h = 40): Sparkline {
  const n = temps.length;
  if (n < 2) return { line: '', area: '', nowX: 0, nowY: h / 2, min: 0, max: 0 };
  const min = Math.min(...temps);
  const max = Math.max(...temps);
  const span = Math.max(max - min, 1);
  const top = 4;
  const bottom = h - 4;
  const y = (t: number) => bottom - ((t - min) / span) * (bottom - top);
  const x = (i: number) => (i / (n - 1)) * w;
  const pts = temps.map((t, i) => `${x(i).toFixed(1)},${y(t).toFixed(1)}`).join(' ');
  const fh = Math.max(0, Math.min(n - 1, fractionalHour));
  const i0 = Math.floor(fh);
  const i1 = Math.min(i0 + 1, n - 1);
  const tNow = temps[i0]! + (temps[i1]! - temps[i0]!) * (fh - i0);
  return {
    line: pts,
    area: `0,${h} ${pts} ${w},${h}`,
    nowX: round1((fh / (n - 1)) * 100),
    nowY: round1(y(tNow)),
    min,
    max,
  };
}

export function precipLabel(prob: number): string {
  return prob >= 20 ? `${prob}%` : '';
}

export function formatWind(ms: number, dir: string): string {
  return `${Math.round(ms)} m/s · ${dir}`;
}

export function formatVisibility(m: number): string {
  if (m >= 10000) return 'Over 10 km';
  if (m >= 1000) return `${Math.round(m / 100) / 10} km`;
  return `${Math.round(m)} m`;
}

export function formatPressure(hpa: number): string {
  return `${Math.round(hpa).toLocaleString('en-US')} hPa`;
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}
